import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ListChecks, Hand, CheckCircle2, ArrowRight, Inbox, User, AlertTriangle, Clock,
} from "lucide-react";
import { sr } from "../../lib/sr";

const STAGE_BADGE = { Plan: "sr-badge-gray", Produce: "sr-badge-amber", Review: "sr-badge-amber", Launch: "sr-badge-green" };

function WorkTask({ t, mineView, busy, onClaim, onRelease, onComplete }) {
  return (
    <div className="sr-card" data-testid={`work-task-${t.id}`} style={{ background: "#fff", display: "flex", alignItems: "center", gap: 12, padding: "12px 14px" }}>
      {t.status === "blocked" ? <AlertTriangle size={16} style={{ color: "#C0362C", flexShrink: 0 }} /> : <Clock size={16} style={{ color: "var(--blue)", flexShrink: 0 }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: "var(--navy)", fontWeight: 600 }}>{t.title}</div>
        <div className="sr-muted" style={{ fontSize: 12, display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
          <span>{t.client_name}</span>
          <span className={`sr-badge ${STAGE_BADGE[t.stage] || "sr-badge-gray"}`} style={{ fontSize: 10 }}>{t.stage}</span>
          <span className="sr-badge sr-badge-gray" style={{ fontSize: 10 }}>{t.required_cap}</span>
        </div>
      </div>
      <Link to={`/app/campaigns/${t.campaign_id}/project`} className="sr-btn sr-btn-ghost sr-btn-sm" data-testid={`open-project-${t.id}`}>Open</Link>
      {mineView ? (
        <>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => onRelease(t.id)} disabled={busy === t.id} data-testid={`release-${t.id}`}>Release</button>
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => onComplete(t.id)} disabled={busy === t.id} data-testid={`complete-${t.id}`}>
            {busy === t.id ? <Loader2 size={14} className="sr-spin" /> : <><CheckCircle2 size={14} /> Done</>}
          </button>
        </>
      ) : (
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => onClaim(t.id)} disabled={busy === t.id} data-testid={`claim-${t.id}`}>
          {busy === t.id ? <Loader2 size={14} className="sr-spin" /> : <><Hand size={14} /> Claim</>}
        </button>
      )}
    </div>
  );
}

export default function WorkQueue() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => sr.workQueue().then(setData).catch((e) => setData({ error: e?.response?.data?.detail || true }));
  useEffect(() => { load(); }, []);

  const claim = async (id) => {
    setBusy(id);
    try { await sr.claimTask(id); toast.success("Task claimed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not claim"); load(); }
    finally { setBusy(null); }
  };
  const release = async (id) => {
    setBusy(id);
    try { await sr.releaseTask(id); toast.success("Released"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not release"); }
    finally { setBusy(null); }
  };
  const complete = async (id) => {
    setBusy(id);
    try { await sr.completeTask(id); toast.success("Marked done"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not complete"); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">{typeof data.error === "string" ? data.error : "Could not load the work queue."}</p></div>;

  const { unassigned, mine, next, counts } = data;

  return (
    <div data-testid="work-queue-page">
      <div style={{ marginBottom: 16 }}>
        <span className="sr-badge" style={{ marginBottom: 8 }}><ListChecks size={12} /> Work queue</span>
        <h1 style={{ fontSize: 30 }}>What should I do next</h1>
        <p className="sr-muted" style={{ marginTop: 6 }}>Claimable work matched to what you are allowed to do, plus everything on your plate.</p>
      </div>

      {next && (
        <div className="sr-card" data-testid="next-best-action" style={{ marginBottom: 18, borderLeft: "4px solid var(--blue)" }}>
          <div className="sr-muted" style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Your next best action</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginTop: 6 }}>
            <div>
              <div style={{ fontSize: 17, color: "var(--navy)", fontWeight: 700 }}>{next.title}</div>
              <div className="sr-muted" style={{ fontSize: 13 }}>{next.client_name} · {next.stage}</div>
            </div>
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => claim(next.id)} disabled={busy === next.id} data-testid="claim-next-btn">
              {busy === next.id ? <Loader2 size={14} className="sr-spin" /> : <>Claim & start <ArrowRight size={14} /></>}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }} className="sr-two-col">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <Inbox size={16} style={{ color: "var(--blue)" }} />
            <strong style={{ color: "var(--navy)" }}>Unassigned</strong>
            <span className="sr-badge sr-badge-gray" data-testid="count-unassigned">{counts.unassigned}</span>
          </div>
          {unassigned.length === 0 ? (
            <div className="sr-card" data-testid="unassigned-empty"><p className="sr-muted" style={{ fontSize: 13 }}>Nothing waiting to be picked up.</p></div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>{unassigned.map((t) => <WorkTask key={t.id} t={t} busy={busy} onClaim={claim} onRelease={release} onComplete={complete} />)}</div>
          )}
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <User size={16} style={{ color: "var(--blue)" }} />
            <strong style={{ color: "var(--navy)" }}>My tasks</strong>
            <span className="sr-badge sr-badge-amber" data-testid="count-mine-pending">{counts.mine_pending} pending</span>
          </div>
          {mine.length === 0 ? (
            <div className="sr-card" data-testid="mine-empty"><p className="sr-muted" style={{ fontSize: 13 }}>You have not claimed anything yet.</p></div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>{mine.map((t) => <WorkTask key={t.id} t={t} mineView busy={busy} onClaim={claim} onRelease={release} onComplete={complete} />)}</div>
          )}
        </div>
      </div>
    </div>
  );
}
